type CloudbaseInitConfig = {
  env: string
  region?: string
  clientId?: string
}

function readConfig(): CloudbaseInitConfig {
  const env = (import.meta.env.VITE_CLOUDBASE_ENV as string | undefined) || ''
  if (!env) throw new Error('缺少 VITE_CLOUDBASE_ENV（CloudBase 环境 ID）配置')
  const region = (import.meta.env.VITE_CLOUDBASE_REGION as string | undefined) || 'ap-shanghai'
  const clientId = (import.meta.env.VITE_CLOUDBASE_CLIENT_ID as string | undefined) || ''
  return { env, region: region || undefined, clientId: clientId || undefined }
}

let appPromise: Promise<any> | null = null

export async function getCloudbaseApp() {
  if (typeof window === 'undefined') throw new Error('CloudBase 仅能在浏览器端初始化')
  if (!appPromise) {
    appPromise = (async () => {
      const cloudbase = (await import('@cloudbase/js-sdk')).default
      const cfg = readConfig()
      const initArg: any = { env: cfg.env }
      initArg.region = 'ap-shanghai'
      initArg.endpoint = 'https://tcb-api.tencentcloudapi.com'
      initArg.endPointMode = 'GATEWAY'
      return cloudbase.init(initArg)
    })()
  }
  return appPromise
}

export async function getCloudbaseAuth() {
  const app = await getCloudbaseApp()
  return app.auth()
}

function getCloudbaseGatewayBase() {
  const cfg = readConfig()
  return `https://${cfg.env}.api.tcloudbasegateway.com`
}

function getCloudbaseClientId() {
  const cfg = readConfig()
  return cfg.clientId || cfg.env
}

function getDeviceId() {
  if (typeof window === 'undefined') return ''
  const k = 'cb_device_id'
  const existing = window.localStorage.getItem(k)
  if (existing) return existing
  let next = ''
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const buf = new Uint8Array(16)
    crypto.getRandomValues(buf)
    next = Array.from(buf)
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('')
  } else {
    next = String(Math.random()).slice(2) + String(Date.now())
  }
  window.localStorage.setItem(k, next)
  return next
}

export type CloudbaseTokens = { accessToken: string; refreshToken: string; expiresIn?: number }

function toTrimmedString(v: unknown): string {
  return typeof v === 'string' ? v.trim() : v === null || v === undefined ? '' : String(v).trim()
}

function toFiniteNumber(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  return Number.isFinite(n) ? n : undefined
}

function parseIsoToMs(v: unknown): number | undefined {
  const s = toTrimmedString(v)
  if (!s) return undefined
  const ms = Date.parse(s)
  return Number.isFinite(ms) ? ms : undefined
}

function extractTokensFromSessionLike(res: any): CloudbaseTokens & { expiresAt?: number } {
  const pickObj = (o: any, path: string[]) => path.reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), o)

  const session =
    pickObj(res, ['data', 'session']) ??
    pickObj(res, ['data', 'credentials']) ??
    pickObj(res, ['session']) ??
    pickObj(res, ['credentials']) ??
    pickObj(res, ['data', 'data', 'session']) ??
    pickObj(res, ['data']) ??
    res

  const accessToken =
    toTrimmedString(pickObj(session, ['access_token'])) ||
    toTrimmedString(pickObj(session, ['accessToken'])) ||
    toTrimmedString(pickObj(res, ['access_token'])) ||
    toTrimmedString(pickObj(res, ['accessToken'])) ||
    ''

  const refreshToken =
    toTrimmedString(pickObj(session, ['refresh_token'])) ||
    toTrimmedString(pickObj(session, ['refreshToken'])) ||
    toTrimmedString(pickObj(res, ['refresh_token'])) ||
    toTrimmedString(pickObj(res, ['refreshToken'])) ||
    ''

  const expiresIn =
    toFiniteNumber(pickObj(session, ['expires_in'])) ??
    toFiniteNumber(pickObj(session, ['expiresIn'])) ??
    toFiniteNumber(pickObj(res, ['expires_in'])) ??
    toFiniteNumber(pickObj(res, ['expiresIn']))

  const expiresAtMs =
    parseIsoToMs(pickObj(session, ['expires_at'])) ??
    parseIsoToMs(pickObj(session, ['expiresAt'])) ??
    parseIsoToMs(pickObj(res, ['expires_at'])) ??
    parseIsoToMs(pickObj(res, ['expiresAt'])) ??
    undefined

  return { accessToken, refreshToken, expiresIn: expiresIn === undefined ? undefined : Math.max(0, expiresIn), ...(expiresAtMs ? { expiresAt: expiresAtMs } : null) }
}

export async function getCloudbaseTokens(): Promise<CloudbaseTokens> {
  const auth = await getCloudbaseAuth()
  try {
    if (typeof (auth as any).getSession === 'function') {
      const sess = await (auth as any).getSession()
      const t = extractTokensFromSessionLike(sess)
      if (t.accessToken) return t
    }
  } catch {}

  try {
    if (typeof (auth as any).getCredentials === 'function') {
      const cred = await (auth as any).getCredentials()
      const t = extractTokensFromSessionLike(cred)
      if (t.accessToken) return t
    }
  } catch {}

  const res = await (auth as any).getAccessToken()
  const accessToken = toTrimmedString((res as any)?.accessToken || (res as any)?.access_token)
  if (!accessToken) throw new Error('获取 accessToken 失败')
  return { accessToken, refreshToken: '' }
}

export async function refreshCloudbaseSession(refreshToken?: string): Promise<CloudbaseTokens & { expiresAt?: number }> {
  const auth = await getCloudbaseAuth()
  const rt = toTrimmedString(refreshToken)

  const isInvalidGrant = (e: any) => {
    const err = String(e?.error ?? e?.code ?? '').toLowerCase()
    const errCode = String(e?.error_code ?? e?.errorCode ?? '').trim()
    const desc = String(e?.error_description ?? e?.message ?? '').toLowerCase()
    return err === 'invalid_grant' || errCode === '4026' || desc.includes('invalid refresh token') || desc.includes('refreshed by other process')
  }

  if (typeof (auth as any).refreshSession === 'function') {
    try {
      const res = await (auth as any).refreshSession()
      const t = extractTokensFromSessionLike(res)
      if (t.accessToken) return t
    } catch (e: any) {
      if (isInvalidGrant(e)) {
        const latest = await getCloudbaseTokens()
        if (latest.accessToken) return latest
      }
      if (!rt) throw e
      try {
        const res = await (auth as any).refreshSession(rt)
        const t = extractTokensFromSessionLike(res)
        if (t.accessToken) return t
      } catch (e2: any) {
        if (isInvalidGrant(e2)) {
          const latest = await getCloudbaseTokens()
          if (latest.accessToken) return latest
        }
        throw e2
      }
    }
  }

  if (typeof (auth as any).refreshTokenForce === 'function') {
    const cred = await (auth as any).refreshTokenForce({ version: 'v1' })
    const t = extractTokensFromSessionLike(cred)
    if (t.accessToken) return t
  }

  const fallback = await getCloudbaseTokens()
  if (!fallback.accessToken) throw new Error('refresh_failed')
  return fallback
}

export async function refreshCloudbaseTokens(refreshToken: string): Promise<CloudbaseTokens> {
  const rt = String(refreshToken || '').trim()
  if (!rt) throw new Error('missing_refresh_token')
  const url = `${getCloudbaseGatewayBase()}/auth/v1/token`
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const deviceId = getDeviceId()
  if (deviceId) headers.set('x-device-id', deviceId)
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ client_id: getCloudbaseClientId(), grant_type: 'refresh_token', refresh_token: rt }),
  })
  const text = await resp.text()
  let json: any = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    json = null
  }
  if (!resp.ok) throw new Error(String(json?.error_description || json?.error || resp.status || 'refresh_failed'))
  const accessToken = String(json?.access_token || json?.accessToken || '').trim()
  const nextRefreshToken = String(json?.refresh_token || json?.refreshToken || '').trim()
  const expiresIn = typeof json?.expires_in === 'number' ? json.expires_in : typeof json?.expiresIn === 'number' ? json.expiresIn : undefined
  if (!accessToken) throw new Error('refresh_failed')
  return { accessToken, refreshToken: nextRefreshToken, expiresIn }
}
